import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import {
  FaShieldAlt,
  FaFileContract,
  FaHandshake,
  FaCheckCircle,
  FaTruck,
  FaUndoAlt,
  FaBuilding,
  FaExclamationTriangle,
  FaCookieBite,
  FaHeadset,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt
} from 'react-icons/fa';

const policySections = [
  {
    id: 'privacy',
    title: '1. Privacy Policy',
    shortTitle: 'Privacy Policy',
    icon: <FaShieldAlt className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">1. Privacy Policy</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.1 Introduction</h5>
        <p className="text-secondary">
          Telering Process Pvt. Ltd. (“Company”, “we”, “our” or “us”) respects the privacy of visitors, customers, suppliers, business partners and other individuals who interact with our website or business.
        </p>
        <p className="text-secondary">
          This Privacy Policy explains how we collect, use, store and share personal information when individuals visit our website, submit enquiries, request quotations or engage with us for the purchase, sale, collection or supply of used office furniture and equipment.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.2 Information We Collect</h5>
        <p className="text-secondary">Depending on your interaction with us, we may collect:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-2"><strong>Personal information:</strong> Name, phone number, email address and business contact details.</li>
          <li className="mb-2"><strong>Business information:</strong> Company name, business address, GST details and other relevant business information.</li>
          <li className="mb-2"><strong>Inventory information:</strong> Item descriptions, quantities, photographs, condition details, model numbers and other information about office furniture or equipment offered for sale or purchase.</li>
          <li className="mb-2"><strong>Transaction information:</strong> Quotations, purchase orders, invoices, delivery details, collection records and relevant payment confirmations.</li>
          <li className="mb-2"><strong>Technical information:</strong> IP address, browser type, device information and website usage data, where collected.</li>
          <li className="mb-2"><strong>Communication records:</strong> Emails, calls, messages, feedback and customer support requests.</li>
        </ul>
        <p className="text-secondary small fst-italic">
          We aim to collect only the information reasonably necessary for the relevant business purpose.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.3 Purpose of Collection</h5>
        <p className="text-secondary">We may use the information collected to:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Respond to enquiries and quotation requests.</li>
          <li className="mb-1">Evaluate offers for bulk purchase or sale of used office items.</li>
          <li className="mb-1">Coordinate inventory inspections, pickup, transportation and delivery.</li>
          <li className="mb-1">Prepare quotations, purchase orders, invoices and business agreements.</li>
          <li className="mb-1">Communicate order status, collection schedules and delivery updates.</li>
          <li className="mb-1">Maintain business records and resolve disputes.</li>
          <li className="mb-1">Improve our website, services and customer experience.</li>
          <li className="mb-1">Meet applicable legal, tax, accounting and contractual obligations.</li>
          <li className="mb-1">Prevent fraud, misuse and unauthorised access.</li>
        </ul>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.4 Sharing of Information</h5>
        <p className="text-secondary">Where necessary and lawful, we may share relevant information with:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Transporters, logistics providers and collection teams.</li>
          <li className="mb-1">Inventory inspection and valuation service providers.</li>
          <li className="mb-1">Suppliers, buyers and authorised business representatives involved in a transaction.</li>
          <li className="mb-1">Website hosting, IT support and communication service providers.</li>
          <li className="mb-1">Accountants, auditors, legal advisers and professional consultants.</li>
          <li className="mb-1">Government authorities or other parties where required by law.</li>
        </ul>
        <p className="text-secondary small fst-italic">
          We share information only to the extent reasonably necessary for the relevant purpose, subject to applicable law.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.5 Data Retention</h5>
        <p className="text-secondary">
          We retain information for as long as reasonably necessary to fulfil the purpose for which it was collected, complete transactions, maintain business and tax records, resolve disputes and meet applicable legal obligations.
        </p>
        <p className="text-secondary">
          When information is no longer required, we will take appropriate steps to delete or securely dispose of it, subject to lawful retention requirements.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.6 Data Security</h5>
        <p className="text-secondary">
          We take reasonable measures to protect personal and business information against unauthorised access, loss, alteration or disclosure. However, no internet transmission or electronic storage method can be guaranteed completely secure.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.7 Cookies and Website Analytics</h5>
        <p className="text-secondary">
          Our website may use essential cookies and analytics technologies to support website functionality and understand how visitors use the website.
        </p>
        <p className="text-secondary">
          Where required by law, appropriate consent will be obtained for non-essential cookies or tracking technologies. Visitors may also manage cookies through their browser settings.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.8 Your Privacy Rights</h5>
        <p className="text-secondary">
          Subject to applicable law, you may request access to, correction of or deletion of your personal information, withdraw consent where processing relies on consent, or raise a privacy concern.
        </p>
        <p className="text-secondary">
          Please contact us at <a href="mailto:support@teleringprocess.in" className="text-primary text-decoration-none fw-semibold">support@teleringprocess.in</a>. We may verify your identity before processing a request.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.9 Third-Party Websites</h5>
        <p className="text-secondary">
          Our website may contain links to external websites. We are not responsible for the privacy practices of websites that we do not control.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.10 Policy Updates</h5>
        <p className="text-secondary">
          We may update this Privacy Policy from time to time. The revised version will be published on our website with the updated date.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">1.11 Contact</h5>
        <div className="bg-light p-3 rounded border">
          <p className="mb-1 fw-bold text-dark">Telering Process Pvt. Ltd.</p>
          <p className="mb-1 text-secondary">
            <FaEnvelope className="me-2 text-primary" />Email: <a href="mailto:support@teleringprocess.in" className="text-decoration-none text-primary">support@teleringprocess.in</a>
          </p>
          <p className="mb-1 text-secondary">
            <FaPhoneAlt className="me-2 text-primary" />Phone: +91 98014 26752
          </p>
          <p className="mb-0 text-secondary">
            <FaMapMarkerAlt className="me-2 text-primary" /><strong>Branch Address:</strong> 78 Bentick Street, 3rd Floor, 3B/9, Krishna Square, Kolkata – 700001, West Bengal, India.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'terms',
    title: '2. Website Terms & Conditions',
    shortTitle: 'Terms & Conditions',
    icon: <FaFileContract className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">2. Website Terms &amp; Conditions</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.1 Acceptance of Terms</h5>
        <p className="text-secondary">
          These Terms &amp; Conditions govern the use of <a href="https://teleringprocess.in/" target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">https://teleringprocess.in/</a> and the general terms under which users may enquire about our business services. By accessing the website, you agree to these Terms. Separate written agreements, purchase orders or quotations may apply to individual transactions.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.2 Nature of Business</h5>
        <p className="text-secondary">
          Telering Process Pvt. Ltd. provides business services relating to the purchase, sale, sourcing, collection, handling and supply of used office furniture and equipment in bulk, subject to the specific arrangement agreed with each customer. Items may include chairs, tables, desks, workstations, cabinets, storage units and other office-related items. The exact scope of our role in any transaction will be specified in the relevant quotation or agreement.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.3 Website Information</h5>
        <p className="text-secondary">
          Descriptions, photographs, quantities, specifications and indicative prices published on the website are provided for general information unless expressly confirmed in a written quotation or agreement. Availability, condition, quantities and prices may change based on stock, inspection, location and other transaction-specific factors.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.4 Quotations and Orders</h5>
        <p className="text-secondary">
          A quotation is subject to the validity period and conditions stated in it. An order will be considered confirmed only when acceptance is communicated through an authorised written confirmation, purchase order or other agreed method. The quotation or order confirmation should specify the items, quantities, agreed prices, applicable taxes, collection or delivery arrangements and payment terms.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.5 Permitted Website Use</h5>
        <p className="text-secondary">Users must not:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Submit false or misleading business or inventory information.</li>
          <li className="mb-1">Use the website for unlawful purposes.</li>
          <li className="mb-1">Attempt unauthorised access to the website or its systems.</li>
          <li className="mb-1">Upload malicious software or interfere with website operations.</li>
          <li className="mb-1">Copy or commercially exploit website content without permission.</li>
          <li className="mb-1">Infringe the rights of the Company or other parties.</li>
        </ul>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.6 Intellectual Property</h5>
        <p className="text-secondary">
          Website content, text, graphics, photographs, designs and other materials are owned by or licensed to the Company unless otherwise stated. Unauthorised copying, distribution or commercial use is prohibited.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.7 Limitation of Website Information</h5>
        <p className="text-secondary">
          We make reasonable efforts to maintain accurate website information but do not guarantee that every listing, photograph, description or indicative price is complete or current at all times. The final terms of a transaction will be determined by the relevant written quotation, inspection record and agreement.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.8 Governing Law</h5>
        <p className="text-secondary">
          These Terms are governed by the laws of India. Disputes will be subject to the jurisdiction of competent courts in India, subject to applicable law.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">2.9 Contact</h5>
        <div className="bg-light p-3 rounded border">
          <p className="mb-1 text-secondary">
            <FaEnvelope className="me-2 text-primary" />Email: <a href="mailto:support@teleringprocess.in" className="text-decoration-none text-primary">support@teleringprocess.in</a>
          </p>
          <p className="mb-1 text-secondary">
            <FaPhoneAlt className="me-2 text-primary" />Phone: +91 98014 26752
          </p>
          <p className="mb-0 text-secondary">
            <FaMapMarkerAlt className="me-2 text-primary" /><strong>Branch Address:</strong> 78 Bentick Street, 3rd Floor, 3B/9, Krishna Square, Kolkata – 700001, West Bengal, India.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'purchase-sale',
    title: '3. Purchase & Sale Policy for Used Office Items',
    shortTitle: 'Purchase & Sale Policy',
    icon: <FaHandshake className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">3. Purchase &amp; Sale Policy for Used Office Items</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.1 Purpose</h5>
        <p className="text-secondary">
          This Policy explains the general process for buying and selling used office furniture and equipment through arrangements with Telering Process Pvt. Ltd. The actual transaction may involve the Company buying items from a seller, selling items to a buyer, sourcing items or coordinating a transaction between parties. The Company's role must be specified in the relevant agreement.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.2 Types of Items</h5>
        <p className="text-secondary">Items may include, subject to availability and acceptance:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Office chairs and visitor chairs.</li>
          <li className="mb-1">Office tables, desks and workstations.</li>
          <li className="mb-1">Conference tables and meeting-room furniture.</li>
          <li className="mb-1">Cabinets, storage racks and drawers.</li>
          <li className="mb-1">Office furniture accessories.</li>
          <li className="mb-1">Other used office furniture or equipment expressly agreed upon.</li>
        </ul>
        <p className="text-secondary small fst-italic">
          We may decline items that are unsafe, materially damaged, incomplete, counterfeit, unlawfully obtained or otherwise unsuitable for the intended transaction.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.3 Seller Responsibilities</h5>
        <p className="text-secondary">A person or business offering items for sale must:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Provide accurate descriptions and quantities.</li>
          <li className="mb-1">Disclose known material defects, damage or missing components.</li>
          <li className="mb-1">Confirm that it has the legal right to sell the items.</li>
          <li className="mb-1">Provide photographs or an inventory list where requested.</li>
          <li className="mb-1">Permit inspection where agreed.</li>
          <li className="mb-1">Disclose any restrictions on access, collection or removal.</li>
          <li className="mb-1">Ensure the items are available for handover at the agreed time.</li>
        </ul>
        <p className="text-secondary small fst-italic">
          Sellers must not knowingly offer stolen goods or items subject to undisclosed third-party ownership claims.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.4 Buyer Responsibilities</h5>
        <p className="text-secondary">A buyer must:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">Review the quotation and item description.</li>
          <li className="mb-1">Confirm the required quantities and specifications.</li>
          <li className="mb-1">Inspect the items where an inspection opportunity is provided.</li>
          <li className="mb-1">Confirm collection or delivery arrangements.</li>
          <li className="mb-1">Make payments according to the agreed terms.</li>
          <li className="mb-1">Arrange suitable access and unloading facilities where required under the agreement.</li>
          <li className="mb-1">Raise concerns about visible damage or shortages within the agreed inspection period.</li>
        </ul>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.5 Inspection and Valuation</h5>
        <p className="text-secondary">
          Used items may be assessed based on their age, condition, functionality, quantity, brand, material, demand, completeness and transportation requirements. Any valuation or purchase offer is subject to the assumptions and conditions stated in the offer.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.6 Ownership and Authority to Sell</h5>
        <p className="text-secondary">
          The seller must have the right to transfer ownership of the items being sold. Where reasonably necessary, the Company may request supporting documentation, an authorised signatory's confirmation or other evidence of ownership or authority to sell.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.7 Pricing and Taxes</h5>
        <p className="text-secondary">
          Prices will be stated in the applicable quotation or order confirmation. The quotation should specify whether the price includes or excludes applicable taxes, transportation, loading, unloading, dismantling, packaging and other agreed costs. Applicable GST and other taxes will be charged or accounted for in accordance with law.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.8 Order Confirmation</h5>
        <p className="text-secondary">
          The final order confirmation should record item descriptions, quantities, agreed price, applicable taxes, inspection/condition details, payment milestones, delivery schedule, and dispute terms.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.9 Cancellation or Modification</h5>
        <p className="text-secondary">
          Requests to change or cancel an order must be communicated in writing. The consequences of cancellation will depend on agreed terms, the stage of performance, and incurred costs.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">3.10 Disputes</h5>
        <p className="text-secondary">
          Any disagreement regarding quantity, condition, pricing, ownership or performance should first be raised in writing. The parties should attempt to resolve the matter through discussion and applicable dispute-resolution processes.
        </p>
      </div>
    )
  },
  {
    id: 'condition-inspection',
    title: '4. Product Condition, Inspection & Quality Policy',
    shortTitle: 'Product Condition & Quality',
    icon: <FaCheckCircle className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">4. Product Condition, Inspection &amp; Quality Policy</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.1 Used Item Classification</h5>
        <p className="text-secondary">
          Used office furniture and equipment may show signs of previous use. These may include scratches, fading, stains, dents, wear, loose fittings, repaired surfaces or other cosmetic imperfections. Unless expressly stated otherwise, used items should not be assumed to be new or factory-refurbished.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.2 Condition Disclosure</h5>
        <p className="text-secondary">Where reasonably available, item descriptions or inspection records may specify:</p>
        <ul className="text-secondary ps-3">
          <li className="mb-1">General physical condition.</li>
          <li className="mb-1">Visible damage or cosmetic wear.</li>
          <li className="mb-1">Completeness of parts and accessories.</li>
          <li className="mb-1">Functional status, where tested.</li>
          <li className="mb-1">Quantity and identifying details.</li>
          <li className="mb-1">Known limitations relevant to use.</li>
        </ul>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.3 Inspection by Buyer</h5>
        <p className="text-secondary">
          Buyers are encouraged to inspect items before accepting the quotation or taking delivery, where inspection is offered. If physical inspection is not practical, the buyer may request available photographs, videos or an inventory description.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.4 Photographs and Descriptions</h5>
        <p className="text-secondary">
          Photographs provide a reasonable visual representation of items. Differences in lighting or display screens may affect how colours and finishes appear. Photographs should not be treated as a substitute for a specific written condition commitment.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.5 Functional Testing</h5>
        <p className="text-secondary">
          Where applicable, functional testing will be limited to checks expressly agreed or recorded. An item described as visually inspected should not automatically be treated as fully tested.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.6 Acceptance and Defects</h5>
        <p className="text-secondary">
          Any inspection and acceptance period must be specified in the order confirmation. Visible shortages or discrepancies should be reported within that agreed period, with supporting photographs or records where possible.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">4.7 Unsafe or Unsuitable Items</h5>
        <p className="text-secondary">
          Items identified as unsafe or unsuitable for ordinary use may be rejected, segregated or handled according to the relevant agreement and applicable law.
        </p>
      </div>
    )
  },
  {
    id: 'pickup-delivery',
    title: '5. Pickup, Transportation & Delivery Policy',
    shortTitle: 'Pickup & Delivery',
    icon: <FaTruck className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">5. Pickup, Transportation &amp; Delivery Policy</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.1 Scope &amp; Scheduling</h5>
        <p className="text-secondary">
          This Policy applies when collection, transportation or delivery of used office items is included in the transaction or separately agreed. Collection and delivery dates are subject to confirmation, site access, item readiness, transport availability and operational requirements.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.2 Site Readiness</h5>
        <p className="text-secondary">
          The responsible party must ensure that the collection or delivery location is accessible at the agreed time (including building access, lift/staircase availability, vehicle access, and permitted working hours).
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.3 Loading and Unloading</h5>
        <p className="text-secondary">
          Responsibility for loading, unloading, dismantling, packing and reassembly must be stated in the quotation or order confirmation. Additional work not included in agreed scope may require separate approval and charges.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.4 Transportation Charges</h5>
        <p className="text-secondary">
          Transportation, labour, packaging and handling costs will be specified in the quotation where applicable.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.5 Delays &amp; Handover Records</h5>
        <p className="text-secondary">
          Collection or delivery may be delayed due to traffic, weather, site restrictions, or vehicle breakdown. We will make reasonable efforts to communicate material delays. Where appropriate, parties will sign a collection or delivery note recording items, quantities, and visible condition.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">5.6 Risk and Responsibility</h5>
        <p className="text-secondary">
          Responsibility for loss or damage during collection and transportation will be determined by the applicable agreement and relevant law.
        </p>
      </div>
    )
  },
  {
    id: 'refund-cancellation',
    title: '6. Refund, Return & Cancellation Policy',
    shortTitle: 'Refund & Cancellation',
    icon: <FaUndoAlt className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">6. Refund, Return &amp; Cancellation Policy</h3>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">6.1 General Principle</h5>
        <p className="text-secondary">
          Used office items are often sold in specific quantities and conditions following inspection, quotation and negotiation. Refunds, returns, replacements and cancellations are governed by the written agreement, the disclosed condition of items and applicable law.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">6.2 Cancellation</h5>
        <p className="text-secondary">
          A quotation request may be withdrawn before an order is accepted. Once confirmed, cancellation or modification must be requested in writing. Any cancellation fee or deduction must be supported by the agreement and law.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">6.3 Returns Due to Discrepancies</h5>
        <p className="text-secondary">
          If delivered items materially differ from the confirmed description, quantity or written condition commitment, notify us promptly within the agreed inspection period. Remedies may include quantity correction, replacement, repair, price adjustment, or refund for affected items.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">6.4 Refund Processing &amp; Deposits</h5>
        <p className="text-secondary">
          Where a refund is approved, it will be processed using the agreed method and within the applicable contractual timeline. Advance payments or deposits must be documented in the quotation or agreement.
        </p>

        <h5 className="h6 fw-bold text-primary mt-4 mb-2">6.5 Contact for Returns and Refunds</h5>
        <div className="bg-light p-3 rounded border">
          <p className="mb-1 text-secondary"><FaEnvelope className="me-2 text-primary" />Email: <a href="mailto:support@teleringprocess.in" className="text-decoration-none text-primary">support@teleringprocess.in</a></p>
          <p className="mb-0 text-secondary"><FaPhoneAlt className="me-2 text-primary" />Phone: +91 98014 26752</p>
        </div>
      </div>
    )
  },
  {
    id: 'partner-terms',
    title: '7. Business Partner, Supplier & Customer Terms',
    shortTitle: 'Partner & Supplier Terms',
    icon: <FaBuilding className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">7. Business Partner, Supplier &amp; Customer Terms</h3>
        <p className="text-secondary">
          Telering Process Pvt. Ltd. may engage with businesses, offices, suppliers, asset owners, buyers, logistics providers and service partners in connection with used office furniture and equipment.
        </p>
        <p className="text-secondary">
          <strong>Supplier Obligations:</strong> Suppliers must provide accurate inventory details, disclose known material defects, confirm authority to sell and comply with agreed collection terms. Suppliers must not knowingly provide counterfeit, stolen or unlawfully obtained items.
        </p>
        <p className="text-secondary">
          <strong>Buyer Obligations:</strong> Buyers must provide accurate billing and delivery details, confirm quantities and specifications, make payments according to agreed terms and cooperate with the agreed inspection and handover process.
        </p>
        <p className="text-secondary">
          <strong>Commercial Terms &amp; Confidentiality:</strong> Prices, service fees, collection charges, transportation costs, and payment milestones must be recorded in the applicable quotation. Parties must protect confidential commercial information.
        </p>
      </div>
    )
  },
  {
    id: 'disclaimer',
    title: '8. Website Disclaimer',
    shortTitle: 'Website Disclaimer',
    icon: <FaExclamationTriangle className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">8. Website Disclaimer</h3>
        <p className="text-secondary">
          The content on <a href="https://teleringprocess.in/" target="_blank" rel="noopener noreferrer" className="text-primary text-decoration-none">https://teleringprocess.in/</a> is provided for general information about the Company's business and services. It is not a binding offer unless expressly stated in an authorised written quotation or agreement.
        </p>
        <p className="text-secondary">
          <strong>Inventory Availability:</strong> References to used office furniture, equipment, stock, quantities or prices do not guarantee availability at the time of enquiry. Availability must be confirmed by the Company or the relevant seller.
        </p>
        <p className="text-secondary">
          <strong>Item Condition:</strong> Used items may have signs of prior use. The actual condition should be determined from the relevant description, photographs, inspection record and agreed condition terms.
        </p>
        <p className="text-secondary">
          <strong>Limitation of Liability:</strong> To the extent permitted by law, the Company is not responsible for losses arising solely from reliance on outdated general website information or temporary website unavailability.
        </p>
      </div>
    )
  },
  {
    id: 'cookies',
    title: '9. Cookie Policy',
    shortTitle: 'Cookie Policy',
    icon: <FaCookieBite className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">9. Cookie Policy</h3>
        <p className="text-secondary">
          Our website may use cookies and similar technologies to support essential functionality, maintain security, remember preferences and analyse website performance.
        </p>
        <ul className="text-secondary ps-3">
          <li className="mb-1"><strong>Essential cookies:</strong> Required for core website functions.</li>
          <li className="mb-1"><strong>Preference cookies:</strong> Used to remember settings.</li>
          <li className="mb-1"><strong>Analytics cookies:</strong> Used to understand visitor traffic.</li>
        </ul>
        <p className="text-secondary">
          Visitors may manage cookies through their browser settings. Disabling certain cookies may affect website functionality.
        </p>
      </div>
    )
  },
  {
    id: 'grievance',
    title: '10. Complaints & Grievance Redressal Policy',
    shortTitle: 'Grievance Redressal',
    icon: <FaHeadset className="me-2 text-primary" />,
    content: (
      <div>
        <h3 className="h4 fw-bold text-dark mb-3">10. Complaints &amp; Grievance Redressal Policy</h3>
        <p className="text-secondary">
          <strong>10.1 Purpose:</strong> Telering Process Pvt. Ltd. aims to handle customer and business complaints fairly, professionally and within applicable contractual and legal requirements.
        </p>
        <p className="text-secondary">
          <strong>10.2 How to Submit a Complaint:</strong> Please contact us with your name, contact details, order or quotation reference, item description, issue details and relevant photographs or supporting documents:
        </p>
        <div className="bg-light p-3 rounded border mb-3">
          <p className="mb-1 text-secondary"><FaEnvelope className="me-2 text-primary" />Email: <a href="mailto:support@teleringprocess.in" className="text-decoration-none text-primary">support@teleringprocess.in</a></p>
          <p className="mb-1 text-secondary"><FaPhoneAlt className="me-2 text-primary" />Phone: +91 98014 26752</p>
          <p className="mb-0 text-secondary"><strong>Working Hours:</strong> Monday to Saturday, 10:00 AM to 7:00 PM</p>
        </div>
        <p className="text-secondary">
          <strong>10.3 Complaint Handling Process:</strong> Upon receiving a complaint, we will acknowledge receipt, review transaction details, request further information if necessary, coordinate with inspection/logistics teams, and communicate next steps.
        </p>
        <p className="text-secondary">
          <strong>10.4 Complaints About Items:</strong> Complaints concerning item condition, quantity, delivery, collection or pricing should refer to the relevant quotation, inspection record, order confirmation and handover documents.
        </p>
        <p className="text-secondary">
          <strong>10.5 Escalation:</strong> If a complaint remains unresolved, it may be escalated to the designated grievance contact at <a href="mailto:support@teleringprocess.in" className="text-primary text-decoration-none">support@teleringprocess.in</a>.
        </p>
        <p className="text-secondary mb-0">
          <strong>10.6 Privacy:</strong> Information submitted during a complaint will be used to investigate and resolve the matter, maintain business records and meet applicable legal requirements.
        </p>
      </div>
    )
  }
];

const PrivacyPolicyPage = () => {
  const [activeTab, setActiveTab] = useState('privacy');
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (location.hash) {
      const targetId = location.hash.replace('#', '');
      if (policySections.some((s) => s.id === targetId)) {
        setActiveTab(targetId);
      }
    }
  }, [location]);

  const activeSection = policySections.find((s) => s.id === activeTab) || policySections[0];

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', paddingTop: '2.5rem', paddingBottom: '4rem' }}>
      <Container>
        <Row>
          {/* Policy Navigation Sidebar */}
          <Col lg={4} xl={3} className="mb-4 mb-lg-0">
            <Card className="shadow-sm border-0 sticky-top" style={{ top: '90px', borderRadius: '1rem' }}>
              <Card.Header
                className="bg-white border-bottom py-3"
                style={{ borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="h6 fw-bold mb-0 text-dark">Document Sections</h5>
                  <Badge bg="light" text="dark" className="border">
                    10 Policies
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-2">
                <Nav variant="pills" className="flex-column gap-1">
                  {policySections.map((sec) => {
                    const isActive = activeTab === sec.id;
                    return (
                      <Nav.Link
                        key={sec.id}
                        active={isActive}
                        onClick={() => setActiveTab(sec.id)}
                        className={`d-flex align-items-center py-2 px-3 rounded text-start transition-all ${
                          isActive
                            ? 'bg-primary text-white shadow-sm fw-semibold'
                            : 'text-secondary hover-bg-light'
                        }`}
                        style={{ cursor: 'pointer', fontSize: '0.92rem' }}
                      >
                        {sec.icon}
                        <span className="text-truncate">{sec.shortTitle}</span>
                      </Nav.Link>
                    );
                  })}
                </Nav>
              </Card.Body>
              <Card.Footer
                className="bg-light py-3 border-top text-center"
                style={{ borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem' }}
              >
                <p className="small text-muted mb-2">Need assistance?</p>
                <a
                  href="mailto:support@teleringprocess.in?subject=Policy%20Enquiry"
                  className="btn btn-outline-primary btn-sm w-100"
                >
                  <FaEnvelope className="me-1" /> Contact Support
                </a>
              </Card.Footer>
            </Card>
          </Col>

          {/* Active Section Content */}
          <Col lg={8} xl={9}>
            <Card className="shadow-sm border-0" style={{ borderRadius: '1rem' }}>
              <Card.Header
                className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center"
                style={{ borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="primary" pill className="px-3 py-1">
                    {activeSection.shortTitle}
                  </Badge>
                  <span className="small text-muted">Telering Process Pvt. Ltd.</span>
                </div>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="btn btn-sm btn-outline-secondary d-none d-md-inline-block"
                  style={{ fontSize: '0.8rem' }}
                >
                  Print
                </button>
              </Card.Header>

              <Card.Body className="p-4 p-md-5">{activeSection.content}</Card.Body>

              <Card.Footer className="bg-light border-top py-3 px-4 d-flex flex-wrap justify-content-between align-items-center">
                <span className="small text-muted">
                  Effective: Feb 3, 2026 | Last Updated: Oct 12, 2026
                </span>
                <span className="small text-muted">
                  Official Document (Sections 1 to 10.6)
                </span>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrivacyPolicyPage;
